import * as React from 'react';
import { ChangeEvent, useState } from 'react';
import { ProcessFileType, TProcessState } from '../../types/Process';
import { simpleDateFormat } from '../../Helpers';
import Description from './panels/Description';
import { fetchWithAuthentication } from '../../actions/BaseAction';
import Planning from './panels/Planning';
import Execution from './panels/Execution';
import Chemical from './panels/Chemical';
import UpdateDate from './panels/UpdateDate';
import ProtectiveMeasures from './panels/ProtectiveMeasures';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from '../../store/Store';
import { TAuthState } from '../../types/Auth';
import { TDispatch } from '../../types/Thunk';
import { uploadProcessImage } from '../../actions/ProcessActions';
import { useUser } from '../../hooks/useUser';
import EntityImageCapture from '../EntityImageCapture';

interface IProcessProps {
  process: TProcessState;
  expandedDescription: boolean;
  expandedPlanning: boolean;
  expandedExecution: boolean;
  expandedChemical: boolean;
  expandedRevised: boolean;
  handleChange: (
    panel: string
  ) => ((event: ChangeEvent<{}>, expanded: boolean) => void) | undefined;
}

const useStyles = makeStyles({
  riskContainer: {
    textAlign: 'center',
    borderRadius: '5px',
    padding: '10px',
    marginTop: '15px',
  },
  riskLabel: {
    fontWeight: 'bold',
    color: '#0000008a',
    marginBottom: '5px',
    textTransform: 'uppercase',
  },
  riskSecondary: {
    marginTop: '5px',
    color: '#0000008a',
  }
});

const Process: React.FC<IProcessProps> = ({
  process,
  expandedDescription,
  expandedPlanning,
  expandedExecution,
  expandedChemical,
  expandedRevised,
  handleChange,
}): JSX.Element => {
  const [urlCache, setUrlCache] = useState<{[key: string]: string}>({});
  const classes = useStyles({});
  const { t } = useTranslation();
  const authState: TAuthState = useSelector<IAppState, TAuthState>(
    (state) => state.authState
  );
  const dispatch = useDispatch<TDispatch>();
  const { isImpersonated } = useUser();

  const getUpdateDate = (date: Date) => {
    return simpleDateFormat(date);
  };

  const handleFileClick = (filePath: string) => () => {
    if (urlCache[filePath]) {
      const cachedBlob = urlCache[filePath];

      window.open(cachedBlob, '_blank');

      return;
    }

    fetchWithAuthentication(filePath)
      .then(blob => {
        if (blob !== null) {
          const blobUrl = URL.createObjectURL(blob);

          setUrlCache((prevCache) => ({ ...prevCache, [filePath]: blobUrl }));

          window.open(blobUrl, '_blank');
        }
      })
      .catch(error => {throw  error});
  }

  if (!process.activeProcess?.data.id) {
    return <></>;
  }

  return (
    <>
      {process && (
        <>
          {process.activeProcess?.data.name && (
            <h2>{process.activeProcess?.data.name as string}</h2>
          )}
          <EntityImageCapture
            imageUrl={process.activeProcess?.data.image as string}
            canEdit={Boolean(authState.data?.permissions?.canEditProcessImages) && !isImpersonated()}
            placeholderIconClass='fas fa-sitemap'
            widthOnDesktop='35%'
            onUpload={(dataUrl) =>
              dispatch(uploadProcessImage(Number(process.activeProcess?.data.id), dataUrl))
            }
            altText={process.activeProcess?.data.name as string}
          />
          {
            process.activeProcess?.data.riskAssessed &&
            process.activeProcess?.data.risk && (
              <div className={classes.riskContainer} style={{
                backgroundColor: process.activeProcess?.data.riskColor,
              }}>
                <p className={classes.riskLabel}>
                  {t(process.activeProcess.data.risk)}
                </p>
                {process.activeProcess?.data.riskDate && (
                  <p className={classes.riskSecondary}>{t('process_risk_score.assessed_date')}: {simpleDateFormat(
                    process.activeProcess.data.riskDate,
                  ) as string}</p>
                )}
              </div>
            )}
        </>
      )}

      {process.activeProcess && (
        <>
          <ProtectiveMeasures
            ppe={process.activeProcess.ppe}
          />
          <ProtectiveMeasures
            ppe={process.activeProcess.hazards}
          />
          <ProtectiveMeasures
            ppe={process.activeProcess.prohibitions}
          />
        </>
      )}

      {process.activeProcess?.data.description && (
        <Description
          description={process.activeProcess.data.description as string}
          expanded={expandedDescription}
          handleChange={handleChange}
          handleFileClick={handleFileClick}
          processFiles={
            Object.values(process.activeProcess.data.processFiles).filter(file => file.type === ProcessFileType.DESCRIPTION)
          }
        />
      )}

      {process.activeProcess?.data.planning && (
        <Planning
          planning={process.activeProcess.data.planning as string}
          expanded={expandedPlanning}
          handleChange={handleChange}
          handleFileClick={handleFileClick}
          processFiles={
            Object.values(process.activeProcess.data.processFiles).filter(file => file.type === ProcessFileType.PLANNING)
          }
        />
      )}

      {process.activeProcess?.data.execution && (
        <Execution
          execution={process.activeProcess.data.execution as string}
          expanded={expandedExecution}
          handleChange={handleChange}
          handleFileClick={handleFileClick}
          processFiles={
            Object.values(process.activeProcess.data.processFiles).filter(file => file.type === ProcessFileType.EXECUTION)
          }
        />
      )}

      {process.activeProcess?.data.chemicals && process.activeProcess?.data.chemicals.length > 0 && (
        <Chemical
          chemicals={process.activeProcess.data.chemicals}
          expanded={expandedChemical}
          handleChange={handleChange}
        />
      )}

      {process.activeProcess?.data.updateDate && (
        <UpdateDate
          updateDate={
            getUpdateDate(
              process.activeProcess.data.updateDate
            ) as string
          }
          expanded={expandedRevised}
          handleChange={handleChange}
        />
      )}
    </>
  );
}

export default Process;